import { interopDefault } from '~/.nuxt/utils'
import { Shops } from '~/config/shops'
import { Routes } from '~/config/routes.types'
import { PRESENTERS_TYPES } from '~/config/presenters'
import { ROUTE_PATHS } from '~/config/routePaths'

/**
 * Helper to create a component loader from a component path
 * Only creates component if the route has a componentPath defined in routePaths
 */
const createComponent = (routeName: string) => {
  const routeConfig = ROUTE_PATHS[routeName]

  if (!routeConfig?.componentPath) {
    return undefined
  }

  // Dynamic imports must be statically analyzable, so we map them here
  const componentMap: Record<string, () => Promise<any>> = {
    'pages/homepage/index.vue': () => import('~/pages/homepage/index.vue'),
    'pages/client/index.vue': () => import('~/pages/client/index.vue'),
    'pages/account/index.vue': () => import('~/pages/account/index.vue'),
    'pages/orders/index.vue': () => import('~/pages/orders/index.vue'),
    'pages/orderDetail/index.vue': () =>
      import('~/pages/orderDetail/index.vue'),
    'pages/reviews/index.vue': () => import('~/pages/reviews/index.vue'),
    'pages/clubs/index.vue': () => import('~/pages/clubs/index.vue'),
    'pages/watchdog/index.vue': () => import('~/pages/watchdog/index.vue'),
    'pages/wishlist/index.vue': () => import('~/pages/wishlist/index.vue'),
    'pages/wishlist/detail.vue': () => import('~/pages/wishlist/detail.vue'),
    'pages/wishlist/public.vue': () => import('~/pages/wishlist/public.vue'),
    'pages/finishAccountDeactivation/index.vue': () =>
      import('~/pages/finishAccountDeactivation/index.vue'),
    'pages/delivery/index.vue': () => import('~/pages/delivery/index.vue'),
    'pages/deliveryDetail/index.vue': () =>
      import('~/pages/deliveryDetail/index.vue'),
    'pages/processChangeEmail/index.vue': () =>
      import('~/pages/processChangeEmail/index.vue'),
    'pages/subscriptionOptout/index.vue': () =>
      import('~/pages/subscriptionOptout/index.vue'),
    'pages/rmaAuthorizedService/index.vue': () =>
      import('~/pages/rmaAuthorizedService/index.vue'),
    'pages/header/index.vue': () => import('~/pages/header/index.vue'),
    'pages/passwordReset/index.vue': () =>
      import('~/pages/passwordReset/index.vue'),
    'pages/registration/index.vue': () =>
      import('~/pages/registration/index.vue'),
    'pages/registration/sent.vue': () =>
      import('~/pages/registration/sent.vue'),
    'pages/registration/finish.vue': () =>
      import('~/pages/registration/finish.vue'),
    'pages/personalDataForms/index.vue': () =>
      import('~/pages/personalDataForms/index.vue'),
    'pages/personalDataForms/sent.vue': () =>
      import('~/pages/personalDataForms/sent.vue'),
    'pages/personalDataForms/verify.vue': () =>
      import('~/pages/personalDataForms/verify.vue'),
  }

  const loader = componentMap[routeConfig.componentPath]

  return loader ? () => interopDefault(loader()) : undefined
}

/**
 * Routes configuration - uses routePaths as single source of truth for paths
 * This file adds presenter names, translations, and creates component loaders
 */
export const ROUTES: Routes = {
  homepage: {
    name: 'homepage',
    aliases: ROUTE_PATHS.homepage.aliases as Record<Shops, string>,
    ...(process.env.SHOW_DEBUG_HOMEPAGE === 'true' && {
      component: createComponent('homepage'),
    }),
    presenterName: PRESENTERS_TYPES.Homepage,
    paths: ROUTE_PATHS.homepage.paths as Record<Shops, string>,
    translations: ['reviews'],
  },
  campaign: {
    name: 'campaign',
    paths: ROUTE_PATHS.campaign.paths as Record<Shops, string>,
  },
  campaignPreviewHashAbTestVariant: {
    name: 'campaignPreviewHashAbTestVariant',
    paths: ROUTE_PATHS.campaignPreviewHashAbTestVariant.paths as Record<
      Shops,
      string
    >,
  },
  author: {
    name: 'author',
    paths: ROUTE_PATHS.author.paths as Record<Shops, string>,
  },
  brand: {
    name: 'brand',
    paths: ROUTE_PATHS.brand.paths as Record<Shops, string>,
  },
  clientCustomerCenter: {
    name: 'clientCustomerCenter',
    component: createComponent('clientCustomerCenter'),
    paths: ROUTE_PATHS.clientCustomerCenter.paths as Record<Shops, string>,
  },
  clientMyAccount: {
    name: 'clientMyAccount',
    component: createComponent('clientMyAccount'),
    presenterName: PRESENTERS_TYPES.Account,
    paths: ROUTE_PATHS.clientMyAccount.paths as Record<Shops, string>,
    translations: ['account', 'smart'],
  },
  clientOrders: {
    name: 'clientOrders',
    component: createComponent('clientOrders'),
    presenterName: PRESENTERS_TYPES.Purchases,
    paths: ROUTE_PATHS.clientOrders.paths as Record<Shops, string>,
    translations: ['common', 'orders', 'reviews', 'digital_content'],
  },
  clientOrderDetail: {
    name: 'clientOrderDetail',
    component: createComponent('clientOrderDetail'),
    paths: ROUTE_PATHS.clientOrderDetail.paths as Record<Shops, string>,
    translations: ['common', 'orders', 'reviews', 'digital_content'],
  },
  reviews: {
    name: 'reviews',
    component: createComponent('reviews'),
    paths: ROUTE_PATHS.reviews.paths as Record<Shops, string>,
    translations: ['common', 'orders', 'reviews'],
  },
  clientCardManagement: {
    name: 'clientCardManagement',
    paths: ROUTE_PATHS.clientCardManagement.paths as Record<Shops, string>,
  },
  clientDigitalLicense: {
    name: 'clientDigitalLicense',
    paths: ROUTE_PATHS.clientDigitalLicense.paths as Record<Shops, string>,
  },
  clientListOfComplaints: {
    name: 'clientListOfComplaints',
    paths: ROUTE_PATHS.clientListOfComplaints.paths as Record<Shops, string>,
  },
  clientLoyalityClubs: {
    name: 'clientLoyalityClubs',
    component: createComponent('clientLoyalityClubs'),
    paths: ROUTE_PATHS.clientLoyalityClubs.paths as Record<Shops, string>,
    translations: ['loyalty'],
  },
  clientVipClub: {
    name: 'clientVipClub',
    paths: ROUTE_PATHS.clientVipClub.paths as Record<Shops, string>,
  },
  clientWatchdog: {
    name: 'clientWatchdog',
    paths: ROUTE_PATHS.clientWatchdog.paths as Record<Shops, string>,
    component: createComponent('clientWatchdog'),
    translations: ['watchdog', 'common', 'orders'],
  },
  clientWishlist: {
    name: 'clientWishlist',
    paths: ROUTE_PATHS.clientWishlist.paths as Record<Shops, string>,
    component: createComponent('clientWishlist'),
    translations: ['wishlist', 'watchdog', 'common', 'orders', 'account'],
  },
  clientWishlistDetail: {
    name: 'clientWishlistDetail',
    paths: ROUTE_PATHS.clientWishlistDetail.paths as Record<Shops, string>,
    component: createComponent('clientWishlistDetail'),
    translations: ['wishlist'],
  },
  publicWishlist: {
    name: 'publicWishlist',
    paths: ROUTE_PATHS.publicWishlist.paths as Record<Shops, string>,
    component: createComponent('publicWishlist'),
    translations: ['wishlist', 'orders'],
  },
  clientFinishAccountDeactivation: {
    name: 'clientFinishAccountDeactivation',
    component: createComponent('clientFinishAccountDeactivation'),
    paths: ROUTE_PATHS.clientFinishAccountDeactivation.paths as Record<
      Shops,
      string
    >,
    translations: ['common', 'account'],
  },
  loyaltyClubDetail: {
    name: 'loyaltyClubDetail',
    presenterName: PRESENTERS_TYPES.LoyaltyClub,
    paths: ROUTE_PATHS.loyaltyClubDetail.paths as Record<Shops, string>,
  },
  orderTrackingSearch: {
    name: 'orderTrackingSearch',
    component: createComponent('orderTrackingSearch'),
    paths: ROUTE_PATHS.orderTrackingSearch.paths as Record<Shops, string>,
    translations: ['common', 'orders'],
  },
  orderTracking: {
    name: 'orderTracking',
    component: createComponent('orderTracking'),
    paths: ROUTE_PATHS.orderTracking.paths as Record<Shops, string>,
    translations: ['common', 'orders'],
  },
  orderTrackingDetail: {
    name: 'orderTrackingDetail',
    component: createComponent('orderTrackingDetail'),
    paths: ROUTE_PATHS.orderTrackingDetail.paths as Record<Shops, string>,
    translations: ['common', 'orders', 'digital_content'],
  },
  userChangeEmail: {
    name: 'userChangeEmail',
    component: createComponent('userChangeEmail'),
    paths: ROUTE_PATHS.userChangeEmail.paths as Record<Shops, string>,
    translations: ['common', 'account'],
  },
  subscriptionOptout: {
    name: 'subscriptionOptout',
    component: createComponent('subscriptionOptout'),
    paths: ROUTE_PATHS.subscriptionOptout.paths as Record<Shops, string>,
    translations: ['common', 'account'],
  },
  rmaAuthorizedServices: {
    name: 'rmaAuthorizedServices',
    paths: ROUTE_PATHS.rmaAuthorizedServices.paths as Record<Shops, string>,
    component: createComponent('rmaAuthorizedServices'),
    translations: ['common', 'rma_services'],
  },
  complaintsInfoPage: {
    name: 'complaintsInfoPage',
    paths: ROUTE_PATHS.complaintsInfoPage.paths as Record<Shops, string>,
  },
  userForgottenPassword: {
    name: 'userForgottenPassword',
    paths: ROUTE_PATHS.userForgottenPassword.paths as Record<Shops, string>,
    component: createComponent('userForgottenPassword'),
    translations: ['account'],
  },
  userRegistration: {
    name: 'userRegistration',
    paths: ROUTE_PATHS.userRegistration.paths as Record<Shops, string>,
    component: createComponent('userRegistration'),
    translations: ['account'],
  },
  userRegistrationSent: {
    name: 'userRegistrationSent',
    paths: ROUTE_PATHS.userRegistrationSent.paths as Record<Shops, string>,
    component: createComponent('userRegistrationSent'),
    translations: ['account'],
  },
  userRegistrationFinish: {
    name: 'userRegistrationFinish',
    paths: ROUTE_PATHS.userRegistrationFinish.paths as Record<Shops, string>,
    component: createComponent('userRegistrationFinish'),
    translations: ['account'],
  },
  userLogin: {
    name: 'userLogin',
    paths: ROUTE_PATHS.userLogin.paths as Record<Shops, string>,
  },
  cart: {
    name: 'cart',
    paths: ROUTE_PATHS.cart.paths as Record<Shops, string>,
  },
  personalData: {
    name: 'personalData',
    paths: ROUTE_PATHS.personalData.paths as Record<Shops, string>,
  },
  privacySettings: {
    name: 'privacySettings',
    paths: ROUTE_PATHS.privacySettings.paths as Record<Shops, string>,
  },
  privacySecurity: {
    name: 'privacySecurity',
    paths: ROUTE_PATHS.privacySecurity.paths as Record<Shops, string>,
  },
  bonusSet: {
    name: 'bonusSet',
    paths: ROUTE_PATHS.bonusSet.paths as Record<Shops, string>,
  },
  header: {
    component: createComponent('header'),
    name: 'header',
    paths: ROUTE_PATHS.header.paths as Record<Shops, string>,
  },
  detailById: {
    name: 'detailById',
    paths: ROUTE_PATHS.detailById.paths as Record<Shops, string>,
  },
  detail: {
    name: 'detail',
    paths: ROUTE_PATHS.detail.paths as Record<Shops, string>,
  },
  detailBrand: {
    name: 'detailBrand',
    paths: ROUTE_PATHS.detailBrand.paths as Record<Shops, string>,
  },
  partner: {
    name: 'partner',
    paths: ROUTE_PATHS.partner.paths as Record<Shops, string>,
  },
  comparing: {
    name: 'comparing',
    paths: ROUTE_PATHS.comparing.paths as Record<Shops, string>,
  },
  search: {
    name: 'search',
    paths: ROUTE_PATHS.search.paths as Record<Shops, string>,
  },
  licence: {
    name: 'licence',
    paths: ROUTE_PATHS.licence.paths as Record<Shops, string>,
  },
  category: {
    name: 'category',
    paths: ROUTE_PATHS.category.paths as Record<Shops, string>,
  },
  personalDataForms: {
    name: 'personalDataForms',
    component: createComponent('personalDataForms'),
    paths: ROUTE_PATHS.personalDataForms.paths as Record<Shops, string>,
    translations: ['personaldataforms'],
  },
  personalDataFormsSent: {
    name: 'personalDataFormsSent',
    component: createComponent('personalDataFormsSent'),
    paths: ROUTE_PATHS.personalDataFormsSent.paths as Record<Shops, string>,
    translations: ['personaldataforms'],
  },
  personalDataFormsVerify: {
    name: 'personalDataFormsVerify',
    component: createComponent('personalDataFormsVerify'),
    paths: ROUTE_PATHS.personalDataFormsVerify.paths as Record<Shops, string>,
    translations: ['personaldataforms'],
  },
}

export default ROUTES
